import { putResponseFeedback } from '@/agent/response_metadata/fetch/put-response-feedback'
import { FeedbackThumb } from '@/chat-common/schemas/feedback-schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { selectAgentConversationFormTemplateFilePaths } from '@/agent/chat_form/store/selectors'
import { RootState } from '@/store/store'
import { MultiSelect, MultiSelectOption } from '@/components/ui/multi-select'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { selectFeedbackReasons } from '@/response-feedback/store/selectors'
import { brandedAIFriendlyName } from '@/util/enterprise'
import { store } from '@/store/store'

import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import clsx from 'clsx'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'

type MessageFeedbackDialogProps = {
  conversationId: string
  eventId?: string
  positive: boolean
  visible: boolean
  onClose: () => void
}

/**
 * The MessageFeedbackDialog collects a thumbs up/down rating (plus optional reasons and free-text
 * details for a thumbs down) on an agent response and submits it via putResponseFeedback.
 */
export default function MessageFeedbackDialog(props: MessageFeedbackDialogProps) {
  const { conversationId, eventId, positive, visible, onClose } = props

  const responseFeedbackTypes = useSelector((state: RootState) => selectFeedbackReasons(state))
  const negativeFeedbackTypes = responseFeedbackTypes.negative_reasons

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState<string>('')
  const [selectedNegativeReasons, setSelectedNegativeReasons] = useState<string[]>([])
  const templateUsed = useSelector((state: RootState) => selectAgentConversationFormTemplateFilePaths(state, { conversationId }).length > 0)
  const { trackEvent } = useAnalytics()

  // Allow submission if the form is valid, and there are actual changes to the form
  const canSubmit = positive || selectedNegativeReasons.length > 0

  // Ref for the textarea to enable autofocus
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const multiselectRef = useRef<HTMLButtonElement | null>(null)

  // Autofocus the textarea when the dialog becomes visible
  useEffect(() => {
    if (visible) {
      // Add a small delay to account for Radix dialog portal mounting and animations
      const timeoutId = setTimeout(() => {
        if (positive && textareaRef.current) {
          textareaRef.current.focus()
        } else if (!positive && multiselectRef.current) {
          multiselectRef.current.focus()
        }
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [visible, positive])

  // Handle submit
  async function handleSubmit() {
    if (!loading) {
      try {
        setError(null)
        setLoading(true)

        // Thumb
        const thumb: FeedbackThumb = positive ? FeedbackThumb.UP : FeedbackThumb.DOWN

        if (!eventId) {
          throw Error('Event ID is required to submit feedback.')
        }
        console.log('Handling feedback submission for event: ', eventId)
        await putResponseFeedback({ eventId: eventId, thumb: thumb, thumbText: text, thumbReasons: selectedNegativeReasons })

        // Perform a 1 second delay
        await new Promise((resolve) => setTimeout(resolve, 1000))

        onClose()

        // analytics if template used
        if (templateUsed) {
          trackEvent(AnalyticsEvent.TemplateInfluencedFeedback, {
            conversationId,
            positive,
          })
        }

        store.dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Thank you for the feedback.', durationMs: kToastDurationShortMs }))
      } catch (error) {
        console.error(error)
        setError('There was an error submitting your feedback. Please try again.')
        store.dispatch(
          openGlobalToast({
            type: GlobalToastType.ERROR,
            message: 'There was an error submitting your feedback. Please try again.',
            durationMs: kToastDurationShortMs,
          })
        )
      } finally {
        setLoading(false)
      }
    }
  }

  // Convert negative feedback types to MultiSelect options
  const negativeFeedbackOptions: MultiSelectOption[] = Object.values(negativeFeedbackTypes).map((type) => ({
    value: type,
    label: type,
  }))

  return (
    <Modal open={visible} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        title="We appreciate your feedback"
        subtitle={!positive ? `Help us understand the issue so we can improve ${brandedAIFriendlyName}.` : undefined}
        width="lg"
        height="max-md"
        preventDismiss
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Cancel', onClick: onClose }}
            primaryAction={{
              label: 'Submit',
              onClick: handleSubmit,
              loading,
              disabled: !canSubmit,
            }}
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
          />
        }
      >
        <div className="space-y-4">
          {/* Multi-select Dropdown for negative feedback reasons */}
          {!positive && (
            <MultiSelect
              ref={multiselectRef}
              options={negativeFeedbackOptions}
              selected={selectedNegativeReasons}
              onChange={setSelectedNegativeReasons}
              placeholder="Select issue type"
            />
          )}

          <div className="relative">
            <label
              className={`absolute left-3 text-brand-neutral-600 pointer-events-none transition-all ${
                text.length === 0 ? 'top-3 text-base' : 'top-2.5 text-xs'
              }`}
            >
              {positive ? 'What did you like about the response?' : 'Additional details (optional)'}
            </label>
            <textarea
              ref={textareaRef}
              className={clsx(
                'w-full min-h-[136px] rounded-[4px] border border-1 border-brand-neutral-400 p-3 text-base resize-none hocus:outline-none hocus:ring-2 hocus:ring-brand-400 text-brand-neutral-950',
                text.length > 0 && 'pt-7'
              )}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
